// @flow
import * as React from 'react';
import {useState} from 'react';
import PropTypes from 'prop-types';

import type {PaymentMethod, Currency} from '../../../../../entities';

import {Link} from 'react-router-dom';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {useEffect} from 'react';
import {ArrowBack} from '@material-ui/icons';
import type {PaymentMethodAction, NiubizRequest} from '../../duck';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import NewFeatureBadge from '../../../../../elements/NewFeatureBadge/NewFeatureBadge';
import Alert from '@material-ui/lab/Alert';
import {Snackbar} from '@material-ui/core';
import {NIUBIZ_SUPPORTED_CURRENCY_CODES} from '../../utils/paymentData';

type Props = {|
  isSaving?: boolean,
  updateNiubizPayLink: (payload: PaymentMethodAction) => void,
  paymentMethod: PaymentMethod,
  currency?: Currency,
  disableActivation?: boolean,
|};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
    // padding: theme.spacing(0, 3),
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  checkbox: {
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    // marginTop: theme.spacing(3),
  },
  descriptionText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  textLink: {
    color: colors.primary,
    fontSize: '0.875rem',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  contactButton: {},
  row: {
    display: 'flex',
    width: '100%',
  },
  textField: {
    marginTop: theme.spacing(2),
    '&:not(:last-child)': {
      marginRight: theme.spacing(2),
    },
  },
  greyText: {
    color: colors.grey600,
    fontSize: '0.75rem',
  },
  messageSpace: {
    margin: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  saveButton: {
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  alert: {
    width: '100%',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

export default function NiubizPayLinkConfig(props: Props) {
  const {
    isSaving,
    updateNiubizPayLink,
    paymentMethod,
    currency,
    disableActivation,
  } = props;

  const classes = useStyles();

  const [disabledButton, setDisabledButton] = useState(false);
  const [showWarningCurrency, setShowWarningCurrency] = useState(false);
  const [enabled, setEnabled] = useState(
    paymentMethod ? paymentMethod.enabled : false
  );

  useEffect(() => {
    if (paymentMethod && paymentMethod.enabled)
      setEnabled(paymentMethod.enabled);
  }, [paymentMethod]);

  const {register, handleSubmit, errors, formState} = useForm({
    mode: 'onChange',
  });
  const {isDirty} = formState;

  const handleToggleEnabled = event => {
    if (event) {
      setEnabled(event.target.checked);
      if (!event.target.checked) {
        const niubizRequest = {
          enabled: false,
        };
        updateNiubizPayLink({
          niubizRequest,
        });
      }
    }
  };

  const handleFormSubmit = data => {
    const niubizRequest: NiubizRequest = {
      enabled: true,
      serverParameters: {
        merchantId: data.merchantId.trim(),
        username: data.username.trim(),
        password: data.password.trim(),
      },
      // clientParameters:
      //   paymentMethod &&
      //   paymentMethod.clientParameters &&
      //   paymentMethod.clientParameters.description === data.description
      //     ? undefined
      //     : {description: data.description || undefined},
    };
    updateNiubizPayLink({
      niubizRequest,
    });
  };

  useEffect(() => {
    // $FlowFixMe
    if (!NIUBIZ_SUPPORTED_CURRENCY_CODES.includes(currency.currency.code)) {
      setShowWarningCurrency(true);
      setDisabledButton(true);
    }
  }, [currency]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={showWarningCurrency}
        autoHideDuration={4000}
        onClose={() => setShowWarningCurrency(false)}
      >
        <Alert severity="warning" onClose={() => setShowWarningCurrency(false)}>
          La moneda configurada en tu tienda no es compatible con Niubiz.
        </Alert>
      </Snackbar>
      <CustomExitPrompt when={isDirty} />
      <div className={classes.subRouteHeader}>
        <IconButton
          variant="contained"
          size="small"
          component={Link}
          to={`/configure/payment-methods${window.location.search}`}
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.subRouteTitle} id="paypal-configuration">
          Niubiz Pay Link
          <NewFeatureBadge />
        </Typography>
      </div>
      <Divider className={classes.divider} />
      {/* {typeof disableActivation === 'boolean' && ( */}
      <FormControlLabel
        classes={{label: classes.checkboxLabel}}
        className={classes.checkbox}
        control={
          <Checkbox
            checked={enabled}
            onChange={handleToggleEnabled}
            color="primary"
            disabled={isSaving}
          />
        }
        label={
          disableActivation
            ? 'Debes configurar tus propias tarifas de envío para habilitar esta opción'
            : 'Activar'
        }
      />
      {/* )} */}

      {enabled && (
        <>
          {/* <Typography className={classes.descriptionText}>
            Coloca las llaves de Niubiz. Si no tienes una cuenta, sigue los{' '}
            <a
              href=""
              target="_blank"
              rel="noopener noreferrer"
            >
              siguientes pasos
            </a>
          </Typography> */}
          <div className={classes.row}>
            <CustomTextField
              className={classes.textField}
              label="ID de Comerciante"
              name="merchantId"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.merchant_id
                  : ''
              }
              error={!!errors.clientId}
              helperText={errors.clientId ? errors.clientId.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={isSaving}
            />
            <CustomTextField
              className={classes.textField}
              label="Nombre de Usuario"
              name="username"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.username
                  : ''
              }
              error={!!errors.clientSecret}
              helperText={
                errors.clientSecret ? errors.clientSecret.message : ''
              }
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={isSaving}
            />
          </div>
          <div className={classes.row}>
            <CustomTextField
              className={classes.textField}
              label="Contraseña"
              name="password"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.password
                  : ''
              }
              error={!!errors.clientSecret}
              helperText={
                errors.clientSecret ? errors.clientSecret.message : ''
              }
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={isSaving}
            />
          </div>
          <CustomButton
            variant="contained"
            color="primary"
            disabled={disabledButton}
            className={classes.saveButton}
            onClick={handleSubmit(handleFormSubmit)}
          >
            Guardar
          </CustomButton>
        </>
      )}
    </div>
  );
}

NiubizPayLinkConfig.propTypes = {
  isSaving: PropTypes.bool,
  paymentMethod: PropTypes.object,
  updateNiubizPayLink: PropTypes.func.isRequired,
  currency: PropTypes.object,
  disableActivation: PropTypes.bool,
};
